import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FolderDto, GalleryDto } from '@nusign/picflow-backend-types';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { FinderComponent } from '../../../account-gallery-shared/components/finder/finder.component';
import { folderNameValidator } from '../../../account/validators/folder-name.validator';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { AnalyticsService } from '../../../services/analytics.service';
import { DialogService, OnGetData } from '../../../services/dialog.service';
import { FolderService } from '../../../services/folder.service';
import { GalleryService } from '../../../services/gallery.service';
import { MoveGalleryService } from '../../../services/move-gallery.service';
import { StateService } from '../../../services/state.service';
import { StatsService } from '../../../services/stats.service';
import { ToastService } from '../../../services/toast.service';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { SwitcherComponent } from '../../../shared/components/switcher/switcher.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { SwitcherItemDirective } from '../../../shared/directives/switcher-item.directive';

@Component({
  selector: 'app-move-gallery-dialog',
  templateUrl: './move-gallery-dialog.component.html',
  styleUrls: ['./move-gallery-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SvgComponent,
    FinderComponent,
    ButtonDirective,
    LoadingIndicatorComponent,
    BillingScopePipe,
    RouterLink,
    SwitcherComponent,
    SwitcherItemDirective,
  ],
})
export class MoveGalleryDialogComponent implements OnGetData {
  private folderService = inject(FolderService);
  private analytics = inject(AnalyticsService);
  private galleryService = inject(GalleryService);
  private moveGalleryService = inject(MoveGalleryService);
  public state = inject(StateService);
  private statsService = inject(StatsService);
  private toasts = inject(ToastService);
  private dialog = inject(DialogService);
  private check = inject(ChangeDetectorRef);
  dialogName = 'move-gallery';
  close: (...args) => void;
  gallery: GalleryDto;
  allFolders: FolderDto[] = [];
  allGalleries: GalleryDto[] = [];
  folders: FolderDto[] = [];
  selectedFolder: FolderDto;
  selectedGallery: GalleryDto;
  initialFolder: FolderDto;
  initialGallery: GalleryDto;
  loading = false;
  form = new FormGroup({
    newFolderName: new FormControl(),
  });
  willCreateSubGallery = false;
  moveTo: GalleryDto | FolderDto;
  hasSubGalleries: boolean;
  initializing = true;
  mode = 'move';

  dialogOptions(): DialogOptions {
    return { size: 640, spacingX: 40, spacingY: 40 };
  }

  async onGetData(
    data: {
      gallery: GalleryDto;
      folders?: FolderDto[];
      galleries?: GalleryDto[];
      selectedFolder?: FolderDto;
      moveTo: GalleryDto | FolderDto;
    },
    close: (...args) => void
  ) {
    this.gallery = data.gallery;
    this.close = close;
    this.allFolders = data.folders || (await firstValueFrom(this.folderService.getFolders()));
    this.allGalleries =
      data.galleries || (await firstValueFrom(this.galleryService.getAllGalleries({ ignorePublicAccessLevel: true })));
    if (this.gallery) {
      this.hasSubGalleries = (await firstValueFrom(this.galleryService.getSubGalleries(this.gallery.id))).length > 0;
    }

    this.form.controls.newFolderName.setValidators(folderNameValidator(this.folders));
    if (this.gallery.folder) {
      this.selectedFolder = this.allFolders.find(f => f.id === this.gallery.folder);
    }
    if (this.gallery.section) {
      const parentGallery = this.allGalleries.find(g => g.id === this.gallery.parent_gallery_id);
      this.selectedGallery = parentGallery;
      if (parentGallery.folder) {
        this.selectedFolder = this.allFolders.find(f => f.id === parentGallery.folder);
      }
    }

    this.initialFolder = this.selectedFolder;
    this.initialGallery = this.selectedGallery;

    if (data.moveTo) {
      const moveTo = data.moveTo;
      if (moveTo.id.startsWith('fold_')) {
        this.onSelectFolder(<FolderDto>moveTo);
      } else {
        this.selectedFolder = this.allFolders.find(f => f.id === (<GalleryDto>moveTo).folder);
        this.onSelectGallery(<GalleryDto>moveTo);
      }
    }

    if (this.selectedFolder) {
      this.folders = this.allFolders.filter(f => f.parent_folder === this.selectedFolder.id);
    } else {
      this.folders = this.allFolders.filter(f => f.parent_folder === null);
    }
    this.initializing = false;
    this.check.detectChanges();
  }

  private async confirmMove() {
    await firstValueFrom(this.moveGalleryService.moveGallery(this.gallery, this.selectedGallery, this.selectedFolder));
    this.analytics.track('gallery_moved');
  }

  private async confirmCopy() {
    const stats = await lastValueFrom(this.statsService.getGalleryStats2(this.gallery.id));

    const total = this.state.plan.storage.total;
    const used = this.state.activeTenant['storage'].active as bigint;
    const remaining = total - used;
    const gallerySize =
      stats.total_assets_original_size_including_subgalleries + stats.total_assets_version_size_including_subgalleries;
    if (gallerySize > remaining) {
      this.toasts.add('Not enough Storage remaining', 'error');
      return false;
    }

    const gallery = await this.galleryService.duplicateGallery(this.gallery.id);
    if (this.selectedFolder?.id === this.initialFolder?.id && this.selectedGallery?.id === this.initialGallery?.id) {
      // Don't need to move it because it already copied to the same place
      return gallery;
    }
    await firstValueFrom(this.moveGalleryService.moveGallery(gallery, this.selectedGallery, this.selectedFolder));
    return gallery;
  }

  changeMode(mode: 'move' | 'copy') {
    this.mode = mode;
    this.check.detectChanges();
  }

  onSelectFolder(folder: FolderDto) {
    this.selectedFolder = folder;
    this.moveTo = folder;
    this.willCreateSubGallery = false;
    this.check.detectChanges();
  }

  onSelectGallery(gallery: GalleryDto) {
    this.selectedGallery = gallery;
    this.willCreateSubGallery = !!gallery;
    if (gallery) {
      this.moveTo = gallery;
    }
    this.check.detectChanges();
  }

  async submit() {
    if (this.loading) return;
    if (
      this.mode === 'move' &&
      this.selectedFolder?.id === this.initialFolder?.id &&
      this.selectedGallery?.id === this.initialGallery?.id
    ) {
      return this.close();
    }
    this.loading = true;
    this.check.detectChanges();
    if (this.mode === 'move') {
      await this.confirmMove();
      this.close({ action: 'moved' });
    } else {
      const resp = await this.confirmCopy();
      if (resp === false) return (this.loading = false);
      this.close({ gallery: resp, action: 'copied' });
    }
  }

  reOpenFn() {
    const { gallery, mode, selectedFolder, moveTo, hasSubGalleries } = this;
    return (_gallery: GalleryDto, _folder: FolderDto) => {
      if (hasSubGalleries) _gallery = undefined;

      this.dialog.open(MoveGalleryDialogComponent, {
        gallery,
        moveTo: _folder || _gallery || moveTo,
        mode,
        selectedFolder: _folder || selectedFolder,
      });
    };
  }
}
